import { Box, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';
import { useCustomTheme } from '../store/theme-context';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { debugLog } from '../utils/debug';

type Props = {
	content: any;
	animationId?: string;
};

const FullWidthImage = ({ content, animationId }: Props) => {
	const { breakpoints, palette } = useTheme();
	const { isHighContrast } = useCustomTheme();
	const [imageHeight, setImageHeight] = useState<number | null>(null);
	const [imageWidth, setImageWidth] = useState<number | null>(null);
	// Obsługa obrazka jako obiektu (ACF) lub stringa (URL)
	const imageUrl = content.image?.url || content.image || '';
	const imageAlt = content.image?.alt || content.image_alt || '';
	const linkUrl = content.link?.url || content.link_url || null;
	const linkTarget = content.link?.target || content.link_target || null;
	const customHeight = content.height || null;
	const animationDuration = content.animation_duration || 20; // domyślnie 20s jak w FullWidthText

	// Obsługa use_easing z ACF - może być boolean, string "1"/"0", lub undefined
	// ACF True/False może zwracać: true/false, 1/0, "1"/"0", lub undefined
	const useEasingRaw = content.use_easing;
	const useEasing =
		useEasingRaw === undefined
			? true // domyślnie true jeśli nie ustawione
			: useEasingRaw === true || useEasingRaw === 1 || useEasingRaw === '1' || useEasingRaw === 'true';

	// Unikalna nazwa animacji dla każdej instancji, aby uniknąć synchronizacji
	const uniqueId = animationId || `full-width-image-${Math.random().toString(36).slice(2, 11)}`;

	// Debug - można usunąć po weryfikacji
	debugLog('[FullWidthImage] animationId:', animationId, 'uniqueId:', uniqueId);
	debugLog('[FullWidthImage] animationDuration:', animationDuration);
	debugLog('[FullWidthImage] use_easing value:', useEasingRaw, 'type:', typeof useEasingRaw, 'parsed:', useEasing);

	// Animacja Test 8b: cubic-bezier(0.2, 0, 0.8, 1) - przetestowana i skalibrowana
	// 0-20%: hamowanie przy wjeździe
	// 20-80%: wolny przejazd przez środek (obrazek w pełni widoczny)
	// 80-100%: przyspieszenie przy wyjeździe
	const slideWithEasing = keyframes`
      0% {
        transform: translateY(-50%) translateX(100vw);
      }
      20% {
        /* max(0, ...) zapobiega ujemnym wartościom gdy obrazek jest węższy niż ekran */
        transform: translateY(-50%) translateX(max(0px, calc(50vw - 50%)));
      }
      80% {
        transform: translateY(-50%) translateX(min(0px, calc(100vw - 100%)));
      }
      100% {
        transform: translateY(-50%) translateX(-100%);
      }
    `;

	// Animacja liniowa (Test 4) - jako fallback
	const slideLinear = keyframes`
      0% {
        transform: translateY(-50%) translateX(100vw);
      }
      100% {
        transform: translateY(-50%) translateX(-100%);
      }
    `;

	// Ładowanie obrazka, aby pobrać jego naturalną wysokość i szerokość
	useEffect(() => {
		if (imageUrl) {
			const img = new Image();
			img.onload = () => {
				setImageHeight(img.naturalHeight);
				setImageWidth(img.naturalWidth);
			};
			img.src = imageUrl;
		}
	}, [imageUrl]);

	// Określenie wysokości sekcji
	const sectionHeight = customHeight || imageHeight || 216; // domyślnie 216px jak w FullWidthText

	// Sprawdzenie, czy link jest wewnętrzny (zaczyna się od /) czy zewnętrzny
	const isInternalLink = linkUrl && linkUrl.startsWith('/');

	if (!imageUrl) {
		return null;
	}

	const imageElement = (
		<Box
			component='img'
			src={imageUrl}
			alt={imageAlt}
			sx={{
				display: 'block',
				height: customHeight ? `${customHeight}px` : 'auto',
				maxHeight: `${sectionHeight}px`,
				width: 'auto',
				objectFit: 'contain',
				flexShrink: 0, // Zapobiega zmniejszaniu obrazka
			}}
		/>
	);

	const scrollableContent = (
		<Box
			display={'flex'}
			alignItems={'center'}
			sx={{
				position: 'absolute',
				top: '50%',
				transform: 'translateY(-50%)',
				// Używamy unikalnego obiektu keyframes dla każdej instancji
				// emotion/react automatycznie generuje unikalną nazwę CSS dla każdego keyframes
				animation: `${useEasing ? slideWithEasing : slideLinear} ${animationDuration}s ${useEasing ? 'cubic-bezier(0.2, 0, 0.8, 1)' : 'linear'} infinite`,
			}}
		>
			{imageElement}
		</Box>
	);

	const containerSx = {
		backgroundColor: isHighContrast
			? palette.primary.light
			: content.background_color
				? content.background_color
				: palette.secondary.light,
		height: `${sectionHeight}px`,
		display: 'flex',
		alignItems: 'center',
		position: 'relative',
		py: '74px',
		textDecoration: 'none',
		cursor: linkUrl ? 'pointer' : 'default',
		overflow: 'hidden',
		width: '100%',
		[breakpoints.down('md')]: {
			py: '56px',
		},
	};

	if (linkUrl && isInternalLink) {
		return (
			<Box
				component={Link}
				to={linkUrl}
				sx={containerSx}
			>
				{scrollableContent}
			</Box>
		);
	}

	if (linkUrl && !isInternalLink) {
		return (
			<Box
				component='a'
				href={linkUrl}
				target={linkTarget || '_blank'}
				rel='noopener noreferrer'
				sx={containerSx}
			>
				{scrollableContent}
			</Box>
		);
	}

	return (
		<Box
			component='section'
			sx={containerSx}
		>
			{scrollableContent}
		</Box>
	);
};

export default FullWidthImage;
